import apiClient from '../api.js';

const resource = '/docente';

/**
 * Endpoints de asistencia del docente.
 *
 * OJO: son endpoints legacy. Reciben los parámetros posicionales separados por
 * coma dentro de la propia URL (no query string ni body) y devuelven siempre
 * `{ resultado: [...] }`. El mensaje de error viaja en `error.response.data.message`
 * (en inglés), NO en `mensaje` como los endpoints nuevos en capas.
 * Se respeta la forma tal cual la expone hoy el backend; cualquier normalización
 * corresponde coordinarla con esa capa, no parchearla acá.
 */
const useDocenteAsistenciaApi = {
    /** Unicódigo del docente logueado. Respuesta: { resultado: [{ unic }] } */
    obtenerUnicodigoDocente(id_sede, usuario) {
        return apiClient.get(`${resource}/extraerUnicodigoDocente/${id_sede},${usuario}`);
    },

    /** Gestiones vigentes. Respuesta: { resultado: [{ id_gestion, nom }] } */
    listarGestionActual() {
        return apiClient.get(`${resource}/listarGestionActual`);
    },

    /**
     * Fechas de una gestión, para poder armar el calendario de clases esperadas.
     * `listarGestionActual` no las trae: sale de una función almacenada cuyo
     * contrato es `{ id_gestion, nom }` y no conviene tocar.
     *
     * OJO: NO es un endpoint legacy como el resto de este archivo. Vive en
     * `UNICEN/modules/academico/`, responde `{ data: {...} }` (no `{ resultado }`)
     * y el error viaja en `mensaje` (no en `message`). Por eso lleva la ruta
     * completa y no el prefijo `resource`.
     *
     * Respuesta: { data: { id_gestion, nombre, fecha_inicio, fecha_fin, periodo,
     *                      activo, cerrada } } — las fechas son ISO o null.
     */
    obtenerGestion(id_gestion) {
        return apiClient.get(`/academico/gestiones/${id_gestion}`);
    },

    /**
     * Grupos asignados al docente en la gestión.
     * Respuesta: { resultado: [{ idgrup, idgppadre, idma, idcarr, idtur,
     *                            carr, matnom, turn, parale, tpgru }] }
     */
    listarGruposDeDocente(unicodigo, id_gestion, id_sede) {
        return apiClient.get(
            `${resource}/listarGruposDeDocentelistas/${unicodigo},${id_gestion},${id_sede}`
        );
    },

    /**
     * Horario del grupo. En los grupos compartidos el horario cuelga del grupo
     * padre: quien llama decide qué id manda.
     * Respuesta: { resultado: [{ id_dia, dias, ... }] }
     */
    listarHorarioDocenteGrupo(unicodigo, id_gestion, id_grupo) {
        return apiClient.get(
            `${resource}/listarHorarioDocenteGrupo/${unicodigo},${id_gestion},${id_grupo}`
        );
    },

    /**
     * Estudiantes del grupo con su estado de asistencia en una fecha puntual.
     * Respuesta: { resultado: [{ col, unic, estu, estad }] }
     */
    listarEstudiantesConAsistencia(id_grupo, id_gestion, id_sede, fecha) {
        return apiClient.get(
            `${resource}/listarEstudianteGrupoAsistencia/${id_grupo},${id_gestion},${id_sede},${fecha}`
        );
    },

    /**
     * Asistencia de TODA la gestión (el endpoint no recibe fecha).
     * Respuesta: { resultado: [{ unic, estu, estad, fechaasis, fecha }] }
     */
    listarAsistenciaGeneralPorGrupo(id_grupo, id_gestion, id_sede) {
        return apiClient.get(
            `${resource}/asistenciaGeneralEstudiantesPorGrupo/${id_grupo},${id_gestion},${id_sede}`
        );
    },

    /** Nómina del grupo. Respuesta: { resultado: [{ fil, unic, est }] } */
    listarNominaDelGrupo(id_grupo, id_gestion, id_sede) {
        return apiClient.get(
            `${resource}/listarEstudianteGrupo/${id_grupo},${id_gestion},${id_sede}`
        );
    },

    /** Da de alta las filas de asistencia del día antes de poder marcarlas. */
    registrarEstudiantesParaAsistencia(id_grupo, id_gestion, fecha) {
        return apiClient.post(
            `${resource}/resitrarEstudianteAsistencia/${id_grupo},${id_gestion},${fecha}`
        );
    },

    /**
     * Marca a un estudiante como PRESENTE o AUSENTE en la fecha.
     * Respuesta: { message: '...' }
     */
    registrarAsistencia(unicodigo, id_grupo, estado, fecha) {
        return apiClient.post(
            `${resource}/addasistencia/${unicodigo},${id_grupo},${estado},${fecha}`
        );
    },

    /**
     * Elimina las filas de asistencia del grupo en esa fecha: deshace el alta de
     * la lista.
     *
     * OJO: igual que `obtenerGestion`, NO es un endpoint legacy. Vive en
     * `UNICEN/modules/academico/`, responde `{ data, mensaje }` (no `{ resultado }`)
     * y el error viaja en `mensaje` (no en `message`). Por eso lleva la ruta
     * completa y parámetros de query reales en vez del estilo de comas en la URL.
     *
     * `unicodigo` es el del docente: no es una credencial —no hay autenticación
     * en el proyecto— sino el dato con el que el backend comprueba contra
     * `grupo_docente` que el grupo le pertenece.
     *
     * `forzar` solo hace falta si la lista ya tiene estudiantes marcados
     * presentes; sin él, esa llamada responde 409.
     *
     * Respuesta: { data: { id_grupo, fecha, filas_eliminadas, presentes_eliminados },
     *              mensaje }
     */
    anularListaDeAsistencia({ id_grupo, fecha, unicodigo, forzar = false }) {
        return apiClient.delete('/academico/asistencias', {
            params: { id_grupo, fecha, unicodigo, forzar },
        });
    },
};

export default useDocenteAsistenciaApi;
